import { useState, useContext, useCallback, useRef } from "react";
import { myAxios } from "../../../../utils/fetch";
import { UserGlobalContextMemorySpace } from "../../../../contexts/user/userGlobalContext";

const LIMIT = 10;

const normalizar = (item) => ({
  ...item,
  fecha: item.fecha_creacion_oc ?? null,
  fechaIntervencion: item.fecha_estimada_intervencion ?? null,
  nombreUsuario: item.nombre_apellido ?? "",
  estado: (item.estado_visual ?? item.estado ?? "").toUpperCase(),
  tipoPedido: item.especialidad ?? "",
});

const useMisAutorizaciones = () => {
  const { user } = useContext(UserGlobalContextMemorySpace);

  const [results, setResults] = useState([]);
  const [loader, setLoader] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroIntegrante, setFiltroIntegrante] = useState("");
  const [filtroTexto, setFiltroTexto] = useState("");

  const principalDni = user?.user?.data?.Customers?.dni ?? "";
  const appToken = user?.user?.data?.appToken ?? "";
  const userToken = user?.user?.data?.Token ?? "";

  // Ref so siguiente()/anterior() always read the current active DNI without stale closure
  const activeDniRef = useRef(null);

  const setActiveDni = useCallback((dni) => {
    activeDniRef.current = dni || null;
  }, []);

  const cargarAutorizaciones = useCallback(
    (currentOffset = 0) => {
      setLoader(true);
      setResults([]);

      const queryDni = activeDniRef.current ?? principalDni;
      const params = { dni: queryDni, limit: LIMIT, offset: currentOffset };
      const query = new URLSearchParams(params).toString();

      myAxios({
        url:
          process.env.REACT_APP_GATEWAY_2 +
          `/patient/api/v1/autorizaciones/getautorizacionesbi?${query}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          USERTOKEN: userToken,
          Authorization: `Bearer ${appToken}`,
        },
      })
        .then((response) => {
          const payload = response?.data?.data ?? {};
          const items = Array.isArray(payload.data) ? payload.data : [];
          setResults(items.map(normalizar));
          setTotal(payload.total ?? 0);
        })
        .catch(() => {
          setResults([]);
          setTotal(0);
        })
        .finally(() => {
          setLoader(false);
        });
    },
    [principalDni, appToken, userToken]
  );

  const siguiente = () => {
    if (offset + LIMIT < total) {
      const newOffset = offset + LIMIT;
      setOffset(newOffset);
      cargarAutorizaciones(newOffset);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const anterior = () => {
    const newOffset = offset - LIMIT;
    if (newOffset >= 0) {
      setOffset(newOffset);
      cargarAutorizaciones(newOffset);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const resultadosFiltrados = results.filter((item) => {
    const matchEstado = filtroEstado
      ? item.estado?.toLowerCase().includes(filtroEstado.toLowerCase())
      : true;
    const matchIntegrante = filtroIntegrante
      ? item.nombreUsuario?.toLowerCase().includes(filtroIntegrante.toLowerCase())
      : true;
    const matchTexto = filtroTexto
      ? item.tipoPedido?.toLowerCase().includes(filtroTexto.toLowerCase()) ||
        item.nro_orden?.toString().includes(filtroTexto)
      : true;
    return matchEstado && matchIntegrante && matchTexto;
  });

  // When client-side member filter is active, total reflects filtered count
  const totalVisible = filtroIntegrante ? resultadosFiltrados.length : total;

  return {
    results: resultadosFiltrados,
    loader,
    total: totalVisible,
    offset,
    limit: LIMIT,
    filtroEstado,
    filtroIntegrante,
    filtroTexto,
    setFiltroEstado,
    setFiltroIntegrante,
    setFiltroTexto,
    setActiveDni,
    cargarAutorizaciones,
    siguiente,
    anterior,
  };
};

export default useMisAutorizaciones;
